import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useTemplateLifecycle } from "src/api/client-api/actionPlanTemplates/useTemplateLifecycle";
import type { TemplateLifecycleOperation } from "src/api/client-api/actionPlanTemplates/useTemplateLifecycle";
import { URLHandler } from "src/services/URLHandler";
import { useSnackbar } from "src/utils/useSnackbar";
import { templateErrorReasons } from "src/api/client-api/actionPlanTemplates/templateErrors";

export const useTemplateLifecycleActions = (id: string) => {
    const mutation = useTemplateLifecycle(id);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const snackbar = useSnackbar();

    const execute = (operation: TemplateLifecycleOperation, onSuccess?: () => void) => {
        if (mutation.isLoading) return;
        mutation.mutate(operation, {
            onSuccess: (template) => {
                const messages = {
                    archive: t("settings.action_plan_templates.archive.success", '"{{name}}" has been archived.', {
                        name: template.name,
                    }),
                    unarchive: t(
                        "settings.action_plan_templates.unarchive.success",
                        '"{{name}}" has been unarchived and is now a draft.',
                        { name: template.name },
                    ),
                    duplicate: t("settings.action_plan_templates.duplicate.success", '"{{name}}" created as a draft.', {
                        name: template.name,
                    }),
                };
                snackbar.setSnackbarProps({ type: "success", message: messages[operation] });
                onSuccess?.();
                if (operation === "duplicate") {
                    navigate(
                        `${URLHandler.settings.configurationPath("platform-setup", "action-plan-templates")}/${template.id}`,
                    );
                }
            },
            onError: (error) => {
                const unavailableSsqVersion =
                    operation === "duplicate" &&
                    templateErrorReasons(error, 422).ssqVersionId === "ssq_version_unavailable";
                const messages = {
                    archive: t(
                        "settings.action_plan_templates.archive.failed",
                        "Unable to archive the template. Please try again.",
                    ),
                    unarchive: t(
                        "settings.action_plan_templates.unarchive.failed",
                        "Unable to unarchive the template. Please try again.",
                    ),
                    duplicate:
                        unavailableSsqVersion ?
                            t(
                                "settings.action_plan_templates.publish.ssq_version_unavailable",
                                "The selected SSQ version is no longer available. Create a new template with another version.",
                            )
                        :   t(
                                "settings.action_plan_templates.duplicate.failed",
                                "Unable to duplicate the template. Please try again.",
                            ),
                };
                snackbar.setSnackbarProps({ type: "error", message: messages[operation] });
            },
        });
    };

    return { execute, isLoading: mutation.isLoading };
};
